import React, { useState, useMemo, useCallback } from 'react';
import { ShareProtocolButton } from '../Category/ShareProtocol';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
/**
 * Collapsible Category Tree Component
 * Displays categories in a hierarchical tree with +/- expansion buttons
 * Shows result counts in parentheses next to each category
 * Includes Quick Search filter to find categories by name
 * NEW: Clicking category title loads its results into the center panel
 * NEW: Share button for generating shareable links
 * NEW: Copy button to duplicate category to user's root level
 */
const CollapsibleCategoryTree = ({
  categories,
  selectedCategories,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onEdit,
  onDelete,
  onViewDetails,
  onCategoryTitleClick,  // NEW: Callback when category title is clicked
  onCategoryCopied,      // NEW: Callback after category is copied
  isOwner = false,
  compact = false,
  token,          // NEW: Token for Share functionality
  showToast       // NEW: Toast function for Share functionality
}) => {
  // Track which categories are expanded
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  // Quick search filter
  const [searchFilter, setSearchFilter] = useState('');

  // Build hierarchy from flat category list
  const categoryTree = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    
    const categoryMap = new Map();
    const roots = [];
    
    // First pass: Create map of all categories
    categories.forEach(cat => {
      const id = cat.id || cat._id;
      categoryMap.set(id, { ...cat, children: [] });
    });
    
    // Second pass: Build tree structure
    categories.forEach(cat => {
      const id = cat.id || cat._id;
      const node = categoryMap.get(id);
      
      if (cat.parent_id && categoryMap.has(cat.parent_id)) {
        categoryMap.get(cat.parent_id).children.push(node);
      } else {
        roots.push(node);
      }
    });
    
    // Sort by name
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    roots.sort(sortByName);
    categoryMap.forEach(node => node.children.sort(sortByName));
    
    return roots;
  }, [categories]);

  // Filter categories by search term (include parents of matching children)
  const filteredTree = useMemo(() => {
    if (!searchFilter.trim()) return categoryTree;
    
    const searchLower = searchFilter.toLowerCase();
    
    const filterNode = (node) => {
      const nameMatches = (node.name || '').toLowerCase().includes(searchLower);
      
      // Recursively filter children
      const filteredChildren = (node.children || [])
        .map(child => filterNode(child))
        .filter(child => child !== null);
      
      // Include node if name matches OR any children match
      if (nameMatches || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      
      return null;
    };
    
    return categoryTree.map(root => filterNode(root)).filter(node => node !== null);
  }, [categoryTree, searchFilter]);

  // Calculate total result count for a category including all descendants
  const getTotalResultCount = (category) => {
    let count = category.result_count || 0;
    if (category.children) {
      category.children.forEach(child => {
        count += getTotalResultCount(child);
      });
    }
    return count;
  };

  // Toggle expand/collapse
  const toggleExpand = (categoryId, e) => {
    e.stopPropagation();
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand all categories
  const expandAll = () => {
    const allIds = new Set();
    const addIds = (cats) => {
      cats.forEach(cat => {
        if (cat.children && cat.children.length > 0) {
          allIds.add(cat.id || cat._id);
          addIds(cat.children);
        }
      });
    };
    addIds(filteredTree);
    setExpandedCategories(allIds);
  };

  // Collapse all categories
  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  if (!categories || categories.length === 0) {
    return (
      <div style={{
        background: 'rgba(253, 224, 71, 0.1)',
        borderRadius: 12,
        padding: 30,
        textAlign: 'center',
        border: '1px dashed rgba(253, 224, 71, 0.3)'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 10, opacity: 0.6 }}>📂</div>
        <p style={{ color: '#a1a1aa', margin: 0 }}>No categories yet</p>
        <p style={{ color: '#71717a', margin: '5px 0 0 0', fontSize: '0.85rem' }}>
          Create your first search category to get started
        </p>
      </div>
    );
  }

  return (
    <div data-testid="collapsible-category-tree">
      {/* Quick Search Filter */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          placeholder="🔍 Search categories..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          data-testid="category-search-filter"
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid rgba(253, 224, 71, 0.3)',
            background: 'rgba(0,0,0,0.3)',
            color: '#0F172A',
            fontSize: '0.85rem',
            outline: 'none'
          }}
        />
        {searchFilter && (
          <div style={{ 
            marginTop: 6, 
            fontSize: '0.75rem', 
            color: '#047857',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Found {filteredTree.length} matching categories</span>
            <button
              onClick={() => setSearchFilter('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              ✕ Clear
            </button>
          </div>
        )}
      </div>

      {/* Control Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 12,
        flexWrap: 'wrap',
        gap: 8
      }}>
        <span style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
          {selectedCategories.length} of {categories.length} selected
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            onClick={expandAll}
            data-testid="expand-all-btn"
            style={{
              background: 'rgba(253, 224, 71, 0.2)',
              border: '1px solid rgba(253, 224, 71, 0.3)',
              color: '#047857',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            + Expand All
          </button>
          <button
            onClick={collapseAll}
            data-testid="collapse-all-btn"
            style={{
              background: 'rgba(253, 224, 71, 0.2)',
              border: '1px solid rgba(253, 224, 71, 0.3)',
              color: '#047857',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            - Collapse All
          </button>
          <button
            onClick={onSelectAll}
            data-testid="select-all-btn"
            style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            data-testid="deselect-all-btn"
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#f87171',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Tree View */}
      <div style={{
        background: 'rgba(0,0,0,0.2)',
        borderRadius: 12,
        padding: compact ? 10 : 15,
        maxHeight: '500px',
        overflowY: 'auto'
      }}>
        {filteredTree.length === 0 && searchFilter ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#a1a1aa' }}>
            <p style={{ margin: 0 }}>No categories match &ldquo;{searchFilter}&rdquo;</p>
          </div>
        ) : (
          filteredTree.map(category => (
            <CategoryTreeNode
              key={category.id || category._id}
              category={category}
              level={0}
              expanded={expandedCategories}
              onToggleExpand={toggleExpand}
              selectedCategories={selectedCategories}
              onToggleSelect={onToggleSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewDetails={onViewDetails}
              onCategoryTitleClick={onCategoryTitleClick}
              onCategoryCopied={onCategoryCopied}
              isOwner={isOwner}
              compact={compact}
              token={token}
              showToast={showToast}
              getTotalResultCount={getTotalResultCount}
            />
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Individual Category Tree Node (recursive)
 */
const CategoryTreeNode = ({
  category,
  level,
  expanded,
  onToggleExpand,
  selectedCategories,
  onToggleSelect,
  onEdit,
  onDelete,
  onViewDetails,
  onCategoryTitleClick,  // NEW: Callback when title is clicked
  onCategoryCopied,      // NEW: Callback after category is copied
  isOwner,
  compact,
  token,       // For Share functionality
  showToast,   // For Share functionality
  getTotalResultCount
}) => {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const categoryId = category.id || category._id;
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expanded.has(categoryId);
  const isSelected = selectedCategories.includes(categoryId);
  const resultCount = category.result_count || 0;
  const totalCount = getTotalResultCount(category);
  const indent = level * (compact ? 16 : 20);

  // Handle copy category
  const handleCopyCategory = async (includeUrls) => {
    if (!token) {
      showToast && showToast('Please log in to copy categories', 'error');
      return;
    }
    
    setCopyLoading(true);
    try {
      const data = await post(`/categories/${categoryId}/copy`, { body: { include_urls: includeUrls } });
      showToast && showToast(
        `${data.message}${includeUrls ? ` (${data.urls_copied} URLs copied)` : ''}`, 
        'success'
      );
      setShowCopyModal(false);
      onCategoryCopied && onCategoryCopied(data);
    } catch (e) {
      showToast && showToast(typeof e === 'string' ? e : 'Failed to copy category', 'error');
    }
    setCopyLoading(false);
  };

  // Handle title click - triggers checkbox AND loads results
  const handleTitleClick = (e) => {
    e.stopPropagation();
    onToggleSelect(categoryId);
    if (onCategoryTitleClick) {
      onCategoryTitleClick(category);
    }
  };

  // Handle checkbox click - only toggles selection
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect(categoryId);
    if (onCategoryTitleClick) {
      onCategoryTitleClick(category);
    }
  };

  return (
    <div data-testid={`category-node-${categoryId}`}>
      {/* Category Row - ULTRA COMPACT */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: compact ? '4px 6px' : '6px 10px',
          marginLeft: indent,
          marginBottom: 1,
          borderRadius: 6,
          background: isSelected 
            ? 'linear-gradient(135deg, rgba(253, 224, 71, 0.3), rgba(245, 158, 11, 0.2))'
            : 'rgba(255, 255, 255, 0.85)',
          border: isSelected 
            ? '1px solid rgba(253, 224, 71, 0.5)'
            : '1px solid transparent',
          cursor: 'pointer',
          transition: 'all 0.1s'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.background = 'rgba(253, 224, 71, 0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
          }
        }}
      >
        {/* Expand/Collapse Button - Smaller */}
        <div
          onClick={(e) => hasChildren && onToggleExpand(categoryId, e)}
          style={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 6,
            borderRadius: 3,
            background: hasChildren ? 'rgba(253, 224, 71, 0.3)' : 'transparent',
            color: hasChildren ? '#047857' : '#52525b',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            cursor: hasChildren ? 'pointer' : 'default',
            flexShrink: 0
          }}
        >
          {hasChildren ? (isExpanded ? '−' : '+') : '•'}
        </div>

        {/* Checkbox - Smaller */}
        <div
          onClick={handleCheckboxClick}
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            background: isSelected ? '#FDE047' : 'rgba(255,255,255,0.1)',
            border: '1.5px solid rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 8,
            flexShrink: 0,
            cursor: 'pointer'
          }}
        >
          {isSelected && <span style={{ color: '#0F172A', fontSize: '0.55rem' }}>✓</span>}
        </div>

        {/* Category Name - CLICKABLE to load results */}
        <div 
          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={handleTitleClick}
          title={`Click to load ${totalCount} results from "${category.name}"`}
        >
          <span style={{ 
            color: isSelected ? '#047857' : '#e2e8f0', 
            fontSize: compact ? '0.8rem' : '0.85rem',
            fontWeight: isSelected ? 600 : 500,
            textDecoration: 'none'
          }}>
            {category.name}
          </span>
          
          {/* Result Count in Parentheses */}
          {totalCount > 0 && (
            <span style={{ 
              color: '#10b981', 
              fontSize: compact ? '0.7rem' : '0.75rem',
              marginLeft: 5,
              fontWeight: 600
            }}>
              ({totalCount})
            </span>
          )}
          
          {/* Child count indicator */}
          {hasChildren && (
            <span style={{ 
              color: '#047857', 
              fontSize: '0.65rem',
              marginLeft: 5
            }}>
              [{category.children.length}]
            </span>
          )}
        </div>

        {/* Privacy Badge - Ultra Compact */}
        {category.privacy && category.privacy !== 'private' && (
          <span style={{
            background: category.privacy === 'public' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
            padding: '1px 4px',
            borderRadius: 4,
            fontSize: '0.55rem',
            color: category.privacy === 'public' ? '#10b981' : '#f59e0b',
            marginLeft: 4,
            flexShrink: 0
          }}>
            {category.privacy === 'public' ? '🌍' : '👥'}
          </span>
        )}

        {/* Action Buttons (for owners) - Always show edit button */}
        {isOwner && (
          <div 
            style={{ display: 'flex', gap: 2, marginLeft: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Copy Button - Always visible for all categories */}
            {token && (
              <button
                onClick={() => setShowCopyModal(true)}
                title="Copy Category"
                data-testid={`copy-category-btn-${categoryId}`}
                style={{
                  background: 'rgba(6, 182, 212, 0.2)',
                  border: 'none',
                  color: '#22d3ee',
                  padding: compact ? '2px 4px' : '3px 6px',
                  borderRadius: 3,
                  fontSize: compact ? '0.55rem' : '0.6rem',
                  cursor: 'pointer'
                }}
              >
                📋
              </button>
            )}
            {/* Share Button */}
            {token && (
              <ShareProtocolButton
                categoryId={categoryId}
                categoryName={category.name}
                protocol={category.protocol || ''}
                token={token}
                showToast={showToast}
              />
            )}
            <button
              onClick={() => onEdit && onEdit(category)}
              title="Edit Category"
              data-testid={`edit-category-btn-${categoryId}`}
              style={{
                background: 'rgba(251, 191, 36, 0.2)',
                border: 'none',
                color: '#047857',
                padding: compact ? '2px 4px' : '3px 6px',
                borderRadius: 3,
                fontSize: compact ? '0.55rem' : '0.6rem',
                cursor: 'pointer'
              }}
            >
              ✏️
            </button>
            {!compact && (
              <button
                onClick={() => onDelete && onDelete(categoryId)}
                title="Delete"
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  color: '#f87171',
                  padding: '2px 4px',
                  borderRadius: 3,
                  fontSize: '0.6rem',
                  cursor: 'pointer'
                }}
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children (if expanded) */}
      {hasChildren && isExpanded && (
        <div>
          {category.children.map(child => (
            <CategoryTreeNode
              key={child.id || child._id}
              category={child}
              level={level + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              selectedCategories={selectedCategories}
              onToggleSelect={onToggleSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewDetails={onViewDetails}
              onCategoryTitleClick={onCategoryTitleClick}
              onCategoryCopied={onCategoryCopied}
              isOwner={isOwner}
              compact={compact}
              token={token}
              showToast={showToast}
              getTotalResultCount={getTotalResultCount}
            />
          ))}
        </div>
      )}

      {/* Copy Category Modal */}
      {showCopyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => !copyLoading && setShowCopyModal(false)}
          data-testid="copy-category-modal"
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1025, #0f0a15)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 450,
              width: '90%',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              color: '#22d3ee', 
              marginBottom: 16, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 10 
            }}>
              📋 Copy Category
            </h3>
            
            <div style={{ 
              background: 'rgba(6, 182, 212, 0.1)', 
              borderRadius: 12, 
              padding: 16, 
              marginBottom: 20 
            }}>
              <p style={{ color: '#334155', margin: 0, marginBottom: 8 }}>
                <strong>Category:</strong> {category.name}
              </p>
              {totalCount > 0 && (
                <p style={{ color: '#a1a1aa', margin: 0, fontSize: '0.9rem' }}>
                  Contains <strong style={{ color: '#10b981' }}>{totalCount}</strong> URL results
                </p>
              )}
            </div>
            
            <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: 20 }}>
              This will create a copy of &ldquo;{category.name}&rdquo; in your categories at the root level.
              {hasChildren && (
                <span style={{ display: 'block', marginTop: 8, color: '#f59e0b' }}>
                  Note: Sub-categories will also be copied.
                </span>
              )}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={() => handleCopyCategory(false)}
                disabled={copyLoading}
                style={{
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(245, 158, 11, 0.3))',
                  border: '1px solid rgba(6, 182, 212, 0.5)',
                  color: '#22d3ee',
                  padding: '14px 20px',
                  borderRadius: 10,
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: copyLoading ? 'wait' : 'pointer',
                  opacity: copyLoading ? 0.6 : 1
                }}
                data-testid="copy-without-urls-btn"
              >
                {copyLoading ? '⏳ Copying...' : '📁 Copy Category Only'}
              </button>
              
              {totalCount > 0 && (
                <button
                  onClick={() => handleCopyCategory(true)}
                  disabled={copyLoading}
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(6, 182, 212, 0.3))',
                    border: '1px solid rgba(16, 185, 129, 0.5)',
                    color: '#10b981',
                    padding: '14px 20px',
                    borderRadius: 10,
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: copyLoading ? 'wait' : 'pointer',
                    opacity: copyLoading ? 0.6 : 1
                  }}
                  data-testid="copy-with-urls-btn"
                >
                  {copyLoading ? '⏳ Copying...' : `📁 Copy with ${totalCount} URLs`}
                </button>
              )}
              
              <button
                onClick={() => setShowCopyModal(false)}
                disabled={copyLoading}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#a1a1aa',
                  padding: '10px 20px',
                  borderRadius: 10,
                  fontSize: '0.9rem',
                  cursor: copyLoading ? 'wait' : 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleCategoryTree;
export { CategoryTreeNode };
